package de.oevbs.kunde.kundenportal2.services.deregistercar

import de.oevbs.businesspartner.bpcrmservice2.partner.model.Country
import de.oevbs.businesspartner.bpcrmservice2.partner.model.Salutation
import de.oevbs.kosmos.ui.elements.button.btn
import de.oevbs.kosmos.ui.forms.inputgroup.inputDate
import de.oevbs.kosmos.ui.forms.inputgroup.inputText
import de.oevbs.kosmos.ui.forms.radiogroup.radioGroupInlined
import de.oevbs.kosmos.ui.forms.selectmenu.selectMenu
import de.oevbs.kosmos.ui.icons.MaterialIconsOutlined
import de.oevbs.kosmos.ui.layouts.card
import de.oevbs.kunde.kundenportal2.GeneralProgressStep
import de.oevbs.kunde.kundenportal2.Pages
import de.oevbs.kunde.kundenportal2.PortalContactNotes
import de.oevbs.kunde.kundenportal2.carContractsButtonToggleFormat
import de.oevbs.kunde.kundenportal2.components.*
import de.oevbs.kunde.kundenportal2.model.services.contract.CarContract
import de.oevbs.kunde.kundenportal2.model.services.deregistercar.*
import de.oevbs.kunde.kundenportal2.navigation.Page
import de.oevbs.kunde.kundenportal2.navigation.visit
import de.oevbs.sfa.data.time.now
import de.oevbs.sfa.data.time.toLocalizedString
import dev.fritz2.core.RenderContext
import dev.fritz2.core.mapNull
import dev.fritz2.headless.validation.Severity
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.map
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.minus

fun RenderContext.deregisterCarStart(page: Page) {
    val deregisterCarStore = DeregisterCarStore(job)

    headerImageLayout {
        title(page.linkTitle)
        content {
            deregisterCarStore.initContractsModel(Unit)
            deregisterCarStore.unsubscribeCarProgressStore.data.render { step ->
                when (step) {
                    GeneralProgressStep.SUBMIT1 -> deregisterCar(deregisterCarStore)
                    GeneralProgressStep.SUCCESS2 -> messagePagePre(
                        "Erfolgreich gemeldet",
                        severity = Severity.Success,
                        preContent = PortalContactNotes.DeregisterCar(
                            deregisterCarStore.current.vehicleAction,
                            deregisterCarStore.current.selectedCarContract?.contractNumberPm ?: "",
                            deregisterCarStore.current.selectedCarContract?.licencePlate ?: "",
                            deregisterCarStore.map(DeregisterCar.unsubDate()).current?.toLocalizedString() ?: "",
                            deregisterCarStore.current.salutationBuyer.value,
                            deregisterCarStore.current.firstnameBuyer,
                            deregisterCarStore.current.lastnameBuyer,
                            deregisterCarStore.current.streetNameBuyer,
                            deregisterCarStore.current.streetNumberBuyer,
                            deregisterCarStore.current.postalCodeBuyer,
                            deregisterCarStore.current.locationBuyer,
                            deregisterCarStore.current.selectedCountry.value
                        ).note
                    )
                }
            }
        }
    }
}

private fun RenderContext.deregisterCar(deregisterCarStore: DeregisterCarStore) {
    contentContainer {
        globalInitTracker.data.combine(deregisterCarStore.initFailedFlow, ::Pair).render { (loading, failed) ->
            if (loading) return@render
            when {
                failed -> noPartnerDataFoundMessage()
                else -> renderDeregisterUi(deregisterCarStore)
            }
        }
    }
}

private fun RenderContext.renderDeregisterUi(deregisterCarStore: DeregisterCarStore) {

    deregisterCarStore.carContractsStore.data.render contracts@{ contracts ->
        if (contracts.isEmpty()) {
            noPartnerDataFoundMessage(
                """Sie haben kein Fahrzeug bei uns versichert. Möchten Sie das ändern? Melden Sie sich
                            | bei Ihrer Beraterin oder Ihrem Berater.""".trimMargin()
            ) {
                btn {
                    label("Mitteilung schreiben")
                }.clicks handledBy {
                    Pages.contactMessage.visit()
                }
            }
            return@contracts
        }

        val vehicleActionStore = deregisterCarStore.map(DeregisterCar.vehicleAction())

        paragraph {
            +"""Ihr Fahrzeug wurde bereits abgemeldet oder verkauft? Teilen Sie uns diese Veränderung ganz einfach über
                        | das folgende Formular mit.""".trimMargin()
        }

        paragraph {
            +"""Die Erstattung eines gegebenenfalls entstehenden Guthabens erfolgt auf das im Vertrag
                        | hinterlegte Konto. Bitte beachten Sie, dass es bei einem Folgevertrag auch  zu einer 
                        | Verrechnung kommen kann. Die Abrechnung des Vertrages erfolgt, wenn uns die Ab-/Ummeldung vom 
                        | Straßenverkehrsamt vorliegt. Sie erhalten dann Post mit weiteren Informationen von uns.
                    """.trimMargin()
        }

        div {
            sectionHeader("Vorgang wählen")
            card {
                imageToggleKupo<String> {
                    value.single(vehicleActionStore)
                    label("")
                    options = VehicleAction.entries.map(VehicleAction::text)
                    optionsFormat = { it }
                    optionsIcon = { option ->
                        when (VehicleAction.entries.find { it.text == option }) {
                            VehicleAction.SOLD -> MaterialIconsOutlined.euro_symbol
                            VehicleAction.SCRAPPED -> MaterialIconsOutlined.recycling
                            VehicleAction.DEREGISTERED -> MaterialIconsOutlined.directions_car
                            else -> MaterialIconsOutlined.error
                        }
                    }
                }
            }
        }

        div {
            sectionHeader("Fahrzeug wählen")
            card {
                div("w-full flex flex-col gap-4") {
                    val selectedCarContractStore = deregisterCarStore
                        .map(DeregisterCar.selectedCarContract())
                        .mapNull(CarContract())

                    buttonToggleKupo<CarContract> {
                        value.single(selectedCarContractStore)
                        options = contracts
                        optionsFormat = carContractsButtonToggleFormat
                    }

                    val now = LocalDate.now()
                    inputDate {
                        // FIXME: Eigentlich soll das Label basierend auf dem Wert der ausgewählten
                        //  "VehicleAction" dynamisch verändert werden, jedoch akzeptiert die LabelProperty
                        //  keinen Flow als Wert. Ein dynamisches Neurendern des gesamten Feldes führt zu einem
                        //  unschönen Flackern der Seite, was mutmaßlich einem Fehler im DatePicker geschuldet
                        //  ist.
                        label("Verkaufs-/Abmeldedatum")
                        value(deregisterCarStore.map(DeregisterCar.unsubDate()))
                        // Erlaubte Eingaben: 1 Jahr in die Vergangheit bis heute
                        min = now.minus(1, DateTimeUnit.YEAR)
                        max = now
                    }
                }
            }
        }

        div {
            vehicleActionStore.data.renderIf(into = this, predicate = { it == VehicleAction.SOLD.text }) {
                sectionHeader("Käufer:innen-Daten")
                card {
                    div("flex flex-col gap-4") {
                        val salutationStore = deregisterCarStore.map(DeregisterCar.salutationBuyer())

                        radioGroupInlined {
                            label("Anrede")
                            options = listOf(Salutation.Frau, Salutation.Herr, Salutation.Firma)
                            optionsFormat = Salutation::value
                            value(salutationStore)
                        }

                        div("grid grid-cols-1 xl:grid-cols-2 gap-4") {
                            salutationStore.data.render { salutation ->
                                if (salutation == Salutation.Firma) {
                                    div("xl:col-span-2") {
                                        inputText {
                                            label("Firmenname")
                                            value(deregisterCarStore.map(DeregisterCar.companyBuyer()))
                                        }
                                    }
                                } else {
                                    inputText {
                                        label("Vorname")
                                        value(deregisterCarStore.map(DeregisterCar.firstnameBuyer()))
                                    }
                                    inputText {
                                        label("Nachname")
                                        value(deregisterCarStore.map(DeregisterCar.lastnameBuyer()))
                                        iconClickableWithPopover(
                                            MaterialIconsOutlined.help,
                                            HelpPopovers.lastname
                                        )
                                    }
                                }
                            }
                            inputText {
                                label("Straße")
                                value(deregisterCarStore.map(DeregisterCar.streetNameBuyer()))
                                iconClickableWithPopover(MaterialIconsOutlined.help, HelpPopovers.street)
                            }
                            inputText {
                                label("Hausnummer")
                                value(deregisterCarStore.map(DeregisterCar.streetNumberBuyer()))
                                iconClickableWithPopover(MaterialIconsOutlined.help, HelpPopovers.streetNumber)
                            }
                            inputText {
                                label("Postleitzahl")
                                value(deregisterCarStore.map(DeregisterCar.postalCodeBuyer()))
                                iconClickableWithPopover(MaterialIconsOutlined.help, HelpPopovers.postalCode)
                            }
                            inputText {
                                label("Ort")
                                value(deregisterCarStore.map(DeregisterCar.locationBuyer()))
                                iconClickableWithPopover(MaterialIconsOutlined.help, HelpPopovers.city)
                            }
                            selectMenu {
                                label("Land")
                                options = Country.all
                                optionsFormat = Country::value
                                value(deregisterCarStore.map(DeregisterCar.selectedCountry()))
                            }
                        }
                    }
                }
            }
        }

        if (contracts.isNotEmpty()) {
            buttonContainer {
                btn {
                    label(vehicleActionStore.data.map {
                        when (it) {
                            VehicleAction.SOLD.text -> "Verkauf mitteilen"
                            VehicleAction.SCRAPPED.text -> "Verschrottung mitteilen"
                            else -> "Abmeldung mitteilen"
                        }
                    })
                    busy(globalSubmitTracker.data)
                }.clicks handledBy deregisterCarStore.submitUnsubscribeRequest
            }
        }
    }
}